import { createSlice } from '@reduxjs/toolkit';

import {
  dealersDetailedMapperFromApi,
  dealersMapperFromApi
} from '@/components/AdminPanel/DealersPoints/helpers/dealersMapper';
import {
  type TDealer,
  type TDealerDetailed,
  type TDealerDto,
  type TDealerFilters
} from '@/components/AdminPanel/DealersPoints/types/dealers.types';
import { DictTypes } from '@/constants';
import { dealersApi } from '@/integrations/adminApi/dealersApi';
import { type StrictOmit } from '@/types/app';

import { createAsyncThunk } from '../create-async-thunk';
import { getOptimizedDictionaries } from '../dict.slice';

type TDealersSlice = {
  dealer: TDealerDetailed | null;
  dealers: TDealer[];
  isLoadingDealers: boolean;
  total: number;
};

const initialState: TDealersSlice = {
  dealer: null,
  dealers: [],
  isLoadingDealers: false,
  total: 0
};

export const getAllDealers = createAsyncThunk(
  'admin/dealers/getAll',
  async (params: TDealerFilters) => {
    return dealersApi.getAdminAllDealers(params);
  }
);

export const getDealerById = createAsyncThunk(
  'admin/getDealerById',
  async (dealerId: string) => {
    return dealersApi.getAdminById(dealerId);
  }
);

export const createDealer = createAsyncThunk(
  'admin/dealers/create',
  async (dealer: StrictOmit<TDealerDto, 'uuid'>, { dispatch }) => {
    const response = await dealersApi.createAdminDealer(dealer);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.Dealers, DictTypes.DealersCities],
        updatingDicts: [DictTypes.Dealers]
      })
    );
    return response;
  }
);

export const updateDealer = createAsyncThunk(
  'admin/dealers/update',
  async (dealer: TDealerDto, { dispatch }) => {
    const response = await dealersApi.updateAdminDealer(dealer);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.Dealers, DictTypes.DealersCities],
        updatingDicts: [DictTypes.Dealers]
      })
    );
    return response;
  }
);

export const adminDealersSlice = createSlice({
  name: 'admin/dealers',
  initialState: initialState,
  reducers: {
    resetDealerState(state) {
      state.dealer = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllDealers.pending, (state) => {
        state.isLoadingDealers = true;
      })
      .addCase(getAllDealers.fulfilled, (state, action) => {
        state.dealers = action.payload.dealers.map(dealersMapperFromApi);
        state.isLoadingDealers = false;
        state.total = action.payload.total;
      })
      .addCase(getAllDealers.rejected, (state) => {
        state.isLoadingDealers = false;
      })
      .addCase(getDealerById.fulfilled, (state, action) => {
        state.dealer = dealersDetailedMapperFromApi(action.payload);
      })
      .addCase(getDealerById.pending, (state) => {
        state.dealer = null;
      });
  }
});

export const { resetDealerState } = adminDealersSlice.actions;
